/**
 * LLM provider failure classification.
 *
 * Pure module — no I/O, no SDK construction — so the chat route, the alert
 * subscriber and the tests all read the SAME classification instead of each
 * re-deriving "is this the billing one?" from a substring.
 *
 * WHY THIS EXISTS. Between 2026-09-02 12:14 and 2026-09-04 09:21 CEST every
 * Klara request failed with `AI_APICallError: Your credit balance is too low to
 * access the Anthropic API` — Anthropic answers that with HTTP **400**, not 402
 * and not 401. The route returned HTTP 200 with an empty stream, nothing
 * alerted, and the recap's Vercel sweep surfaced it five days later. Hence the
 * billing test is on the MESSAGE: no status code identifies that failure.
 *
 * @see knowledgebase/architecture/ARCH-009-domain-events.md (the alert path)
 */

/**
 * How a provider call failed, coarse enough to decide what a human must do.
 *
 * - `billing`  — the account cannot pay (credit balance, spend cap). Needs money.
 * - `auth`     — the key is missing, wrong or revoked. Needs a new key.
 * - `rate_limit` — 429. Usually self-healing.
 * - `server`   — provider 5xx. Usually self-healing.
 * - `unknown`  — anything else, including our own bad requests.
 */
export type ProviderFailureKind = "billing" | "auth" | "rate_limit" | "server" | "unknown";

/** Every value {@link classifyProviderError} can return. */
export const PROVIDER_FAILURE_KINDS: readonly ProviderFailureKind[] = [
  "billing",
  "auth",
  "rate_limit",
  "server",
  "unknown",
] as const;

export interface ProviderFailure {
  kind: ProviderFailureKind;
  /** HTTP status from the provider, when the error carried one. */
  statusCode?: number;
  /** Provider-supplied message, trimmed. Never contains user input. */
  message: string;
}

/** Substrings Anthropic uses for a payment problem, all seen on HTTP 400. */
const BILLING_MARKERS = ["credit balance", "billing", "payment required", "quota exceeded"];

/**
 * Read the pieces of an unknown error that matter here.
 *
 * Duck-typed rather than `APICallError.isInstance()` so this module stays free
 * of an `ai` import (it is loaded on the chat request path) — but the fields
 * read are exactly the ones `APICallError` carries, and the tests classify REAL
 * `APICallError` instances to prove the duck-typing matches the shipped shape.
 */
function readError(error: unknown): {
  name: string;
  message: string;
  statusCode?: number;
  responseBody?: string;
} {
  const e = (error ?? {}) as {
    name?: unknown;
    message?: unknown;
    statusCode?: unknown;
    responseBody?: unknown;
  };
  return {
    name: typeof e.name === "string" ? e.name : "",
    message: typeof e.message === "string" ? e.message : String(error ?? ""),
    statusCode: typeof e.statusCode === "number" ? e.statusCode : undefined,
    responseBody: typeof e.responseBody === "string" ? e.responseBody : undefined,
  };
}

/**
 * Classify a failure from the LLM provider.
 *
 * The message test runs BEFORE the status-code tests, and that is a policy
 * rather than an observation about Anthropic's status codes: a payment problem
 * is a payment problem whatever status carries it. The one outage on record
 * arrived as a 400, which no status branch would have claimed anyway — but a
 * spend-cap refusal delivered as 429 or 403 WOULD be claimed, and would then be
 * classified as a kind that deliberately never alerts. Pinned by
 * "a credit-balance message wins over the status code" in the tests.
 */
export function classifyProviderError(error: unknown): ProviderFailure {
  const { name, message, statusCode, responseBody } = readError(error);
  const haystack = `${message} ${responseBody ?? ""}`.toLowerCase();

  let kind: ProviderFailureKind;

  if (BILLING_MARKERS.some((marker) => haystack.includes(marker))) {
    kind = "billing";
  } else if (
    statusCode === 401 ||
    statusCode === 403 ||
    // Thrown by the provider before any HTTP call when ANTHROPIC_API_KEY is
    // unset — no status code to key on, and exactly as fatal as a 401.
    name === "AI_LoadAPIKeyError" ||
    haystack.includes("invalid x-api-key") ||
    haystack.includes("authentication_error")
  ) {
    kind = "auth";
  } else if (statusCode === 429) {
    kind = "rate_limit";
  } else if (statusCode !== undefined && statusCode >= 500) {
    kind = "server";
  } else {
    kind = "unknown";
  }

  return {
    kind,
    statusCode,
    message: message.trim().slice(0, 500),
  };
}

/**
 * Machine-readable code the chat route puts in the error body, so the browser
 * picks its German copy from a code instead of matching on prose.
 */
export const PROVIDER_UNAVAILABLE_CODE = "provider_unavailable";

/**
 * What the user sees when Klara cannot reach the model. Sent from the server so
 * the same sentence lands in the transcript row and on screen.
 */
export const PROVIDER_UNAVAILABLE_MESSAGE =
  "Klara ist gerade nicht erreichbar. Bitte versuchen Sie es in ein paar Minuten erneut " +
  "oder schreiben Sie uns an info@oekopunktemarkt.de.";

/**
 * The body the chat route hands the client for a provider failure.
 *
 * JSON, because the AI SDK delivers a stream error to the browser as a bare
 * string (`new Error(errorText)`) — the only way to carry a code alongside the
 * text through that seam.
 */
export function providerUnavailableBody(): string {
  return JSON.stringify({
    error: PROVIDER_UNAVAILABLE_MESSAGE,
    code: PROVIDER_UNAVAILABLE_CODE,
  });
}
